import {
    GreenplumFormItemValues,
    HostTypeFiled,
} from 'components/calculator/GreenplumForm/GreenplumFormItem/GreenplumFormItem';
import CalculatorFormLayout from 'components/calculator/CalculatorFormLayout/CalculatorFormLayout';
import {Field} from 'react-final-form';
import {FormFieldRadioButton, FormFieldSelect} from '@yandex-data-ui/cloud-components';
import {toSelectItems} from 'components/formFields/utils';
import SizeRangeStepInput from 'components/calculator/MDBForm/MDBFormCommon/SizeRangeStepInput';
import SizeRangeInput from 'components/calculator/MDBForm/MDBFormCommon/SizeRangeInput';
import HostsInput from 'components/calculator/MDBForm/MDBFormCommon/HostsInput';
import React from 'react';
import {compose} from 'recompose';
import withFinalForm, {WithFinalForm} from 'hoc/withFinalForm';
import {GreenplumFormValues} from 'components/calculator/GreenplumForm/GreenplumForm';
import withTranslation, {WithTranslationProps} from 'hoc/withTranslation';
import {findClosest, getValueInRange} from 'utils';
import {getMemSizeLabel} from 'components/calculator/MDBForm/utils';
import {I18nFunction, I18nKFunction} from 'i18n';
import {
    GreenplumPreparedConfig,
    NormPresetZoneDisk,
    NormResourcePreset,
} from 'components/calculator/GreenplumForm/utils';
import {DiskSize} from 'components/calculator/GreenplumForm/types';
import {
    DISK_TYPE_ID_ORDER,
    GENERATION_ID_MAP,
    GENERATION_ORDER,
    PRESET_TYPE_ORDER,
} from 'components/calculator/GreenplumForm/constants';

interface OptionsProps {
    content: string;
    value: string;
}

interface DiskTypeProps {
    title: string;
    value: string;
}

export interface HostState {
    platforms: OptionsProps[];
    presets: {
        presetId: string;
        diskTypes: NormPresetZoneDisk[];
        content: string;
        value: string;
    }[];
    hostTypes: OptionsProps[];
    hostsRange: Pick<NormPresetZoneDisk, 'minHosts' | 'maxHosts'>;
    diskTypes: DiskTypeProps[];
    diskSizes?: {
        sizes: number[];
    };
    diskSizeRange?: {
        min: number;
        max: number;
    };
}

interface GreenplumHostFormOwnProps {
    hostType: HostTypeFiled;
    index: number;
    data: GreenplumPreparedConfig;
    name: string;
    values: GreenplumFormItemValues[HostTypeFiled];
    onPlatformChange?: (platform: string) => void;
    forwardRef?: React.MutableRefObject<GreenplumHostFormRef | null>;
}

interface Preset {
    presetId: string;
    diskTypes: NormPresetZoneDisk[];
    content: string;
    value: string;
}

export type GreenplumHostFormProps = GreenplumHostFormOwnProps &
    WithTranslationProps &
    WithFinalForm<GreenplumFormValues>;

export interface GreenplumHostFormRef {
    sync: (platform: string) => void;
}

const GreenplumFormItemHost: React.FC<GreenplumHostFormProps> = (props) => {
    const {
        index,
        values,
        data,
        name,
        hostType,
        i18n,
        i18nK,
        formApi,
        onPlatformChange,
        forwardRef,
    } = props;

    const getPresets = React.useCallback(
        (generation: string, type: string) => {
            const {resourcePresets, presetZoneDiskTypes} = data;

            return resourcePresets
                .filter((preset) => {
                    return preset.generation === generation && preset.type === type;
                })
                .map((preset) => {
                    const diskTypes = presetZoneDiskTypes.get(preset) || [];
                    const {presetId} = preset;

                    const result: Preset = {
                        presetId,
                        diskTypes,
                        content: getPresetName(i18n, i18nK, preset),
                        value: presetId,
                    };
                    return result;
                });
        },
        [i18n, i18nK, data],
    );

    const getHostTypesByPlatform = React.useCallback(
        (platform: string) => {
            const {availablePresetTypes} = data;
            return PRESET_TYPE_ORDER.filter((type) => {
                return availablePresetTypes.has(type) && getPresets(platform, type).length;
            }).map((type) => {
                return {
                    content: type,
                    value: type,
                };
            });
        },
        [data, getPresets],
    );

    const getDiskInfo = React.useCallback(
        (presetId: string, diskTypeId: string) => {
            const {idResourcePresetMap, presetZoneDiskTypes} = data;

            const preset = idResourcePresetMap.get(presetId);
            if (!preset) {
                throw new Error('Preset is not found');
            }

            const diskTypes = presetZoneDiskTypes.get(preset) || [];

            const disk = diskTypes.find((_disk) => _disk.diskTypeId === diskTypeId);
            if (!disk) {
                throw new Error('Disk is not found');
            }

            const {minHosts, maxHosts} = disk;

            let diskSizeRange;
            let diskSizes;
            if (disk.DiskSize === DiskSize.DISK_SIZE_RANGE) {
                diskSizeRange = disk.diskSizeRange;
            }
            if (disk.DiskSize === DiskSize.DISK_SIZES) {
                diskSizes = disk.diskSizes;
            }

            return {
                hostsRange: {
                    minHosts,
                    maxHosts,
                },
                diskSizeRange,
                diskSizes,
            };
        },
        [data],
    );

    const [state, setState] = React.useState<HostState>(() => {
        const {platform, disk, presetId, type} = values;
        const {availableGenerations, generationNameMap} = data;

        const platforms = getAvailablePlatforms(i18n, availableGenerations, generationNameMap);
        const hostTypes = getHostTypesByPlatform(platform);
        const presets = getPresets(platform, type);
        const preset = getValidPreset(presets, presetId);
        const diskTypes = getDiskTypes(preset);
        const {hostsRange, diskSizeRange, diskSizes} = getDiskInfo(preset.presetId, disk);

        const stateValue: HostState = {
            platforms,
            presets,
            hostTypes,
            hostsRange,
            diskSizeRange,
            diskTypes,
            diskSizes,
        };

        return stateValue;
    });

    const changeFormValues = React.useCallback(
        (formValues: Array<{name: string; value: Parameters<typeof formApi.change>[1]}>) => {
            const {change} = formApi;

            // eslint-disable-next-line @typescript-eslint/no-shadow
            formValues.forEach(({name, value}) => {
                change(`items[${index}].${name}` as keyof GreenplumFormValues, value);
            });
        },
        [index, formApi],
    );

    const getPresetChangeData = React.useCallback(
        (
            presets: Preset[],
            presetId: string,
            diskType: string,
        ): {
            newState: Partial<HostState>;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            newValues: {name: string; value: any}[];
        } => {
            const {size, hosts} = values;

            const preset = getValidPreset(presets, presetId);
            const diskTypes = getDiskTypes(preset);
            const newDiskType = getValidDiskType(diskTypes, diskType);
            const {diskSizeRange, diskSizes, hostsRange} = getDiskInfo(
                preset.presetId,
                newDiskType,
            );
            const newSize = diskSizeRange
                ? getValueInRange(diskSizeRange, size)
                : findClosest(diskSizes?.sizes || [], size);
            const newHosts = getValueInRange(
                {min: hostsRange.minHosts, max: hostsRange.maxHosts},
                Number(hosts),
            );

            return {
                newState: {
                    diskTypes,
                    diskSizeRange,
                    diskSizes,
                    hostsRange,
                },
                newValues: [
                    {name: `${hostType}.disk`, value: newDiskType},
                    {name: `${hostType}.size`, value: newSize},
                    {name: `${hostType}.hosts`, value: newHosts},
                ],
            };
        },
        [hostType, values, getDiskInfo],
    );

    const getPlatformChangeData = React.useCallback(
        (platform: string, type: string, presetId: string, disk: string) => {
            const presets = getPresets(platform, type);
            const newPreset = getValidPreset(presets, presetId);

            const {newState, newValues} = getPresetChangeData(presets, newPreset.presetId, disk);

            newState.presets = presets;
            newValues.push({name: `${hostType}.presetId`, value: newPreset.presetId});
            if (disk) {
                newValues.push({name: `${hostType}.disk`, value: disk});
            }

            return {newState, newValues};
        },
        [hostType, getPresetChangeData, getPresets],
    );

    const getTypeChangeData = React.useCallback(
        (platform: string, type: string, presetId: string, disk: string) => {
            const hostTypes = getHostTypesByPlatform(platform);
            const hostTypesArray = hostTypes.map(({value}) => value);
            const newType = hostTypesArray.includes(type) ? type : hostTypesArray[0];

            const presets = getPresets(platform, newType);
            const newPreset = getValidPreset(presets, presetId);

            const diskTypes = getDiskTypes(newPreset);
            const newDiskType = getValidDiskType(diskTypes, disk);

            const {newState, newValues} = getPlatformChangeData(
                platform,
                newType,
                presetId,
                newDiskType,
            );

            newState.diskTypes = diskTypes;
            newState.hostTypes = hostTypes;

            return {newState, newValues};
        },
        [getHostTypesByPlatform, getPresets, getPlatformChangeData],
    );

    const handlePlatformChange = React.useCallback(
        (newPlatform?: string) => {
            const {type, presetId, disk} = values;

            if (!newPlatform) {
                return;
            }

            const {newState, newValues} = getTypeChangeData(newPlatform, type, presetId, disk);

            changeFormValues(newValues);
            setState((currentState) => ({...currentState, ...newState}));

            if (onPlatformChange) {
                onPlatformChange(newPlatform);
            }
        },
        [getTypeChangeData, changeFormValues, onPlatformChange, values],
    );

    React.useImperativeHandle(
        forwardRef,
        () => {
            return {
                sync: (platform: string) => {
                    formApi.change(
                        `${name}.${hostType}.platform` as keyof GreenplumFormValues,
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        platform as any,
                    );
                    handlePlatformChange(platform);
                },
            };
        },
        [name, hostType, handlePlatformChange, formApi],
    );

    const handleTypeChange = React.useCallback(
        (newType: string) => {
            const {platform, presetId, disk} = values;

            const {newState, newValues} = getTypeChangeData(platform, newType, presetId, disk);

            changeFormValues(newValues);
            setState((currentState) => ({...currentState, ...newState}));
        },
        [getTypeChangeData, changeFormValues, values],
    );

    const handlePresetChange = React.useCallback(
        (newPreset?: string) => {
            const {platform, type, disk} = values;
            if (!newPreset) {
                return;
            }

            const presets = getPresets(platform, type);

            const {newState, newValues} = getPresetChangeData(presets, newPreset, disk);

            changeFormValues(newValues);
            setState((currentState) => ({...currentState, ...newState}));
        },
        [getPresetChangeData, getPresets, changeFormValues, values],
    );

    const handleDiskChange = React.useCallback(
        (newDisk?: string) => {
            const {platform, type, presetId} = values;

            if (!newDisk) {
                return;
            }

            const {newState, newValues} = getPlatformChangeData(platform, type, presetId, newDisk);

            changeFormValues(newValues);
            setState((currentState) => ({...currentState, ...newState}));
        },
        [getPlatformChangeData, changeFormValues, values],
    );

    const {presets, diskTypes, diskSizes, diskSizeRange, hostsRange, hostTypes, platforms} = state;

    const hostsLabels = React.useMemo(() => {
        return [i18nK('field_mdb-hosts-label', {count: hostsRange.maxHosts})];
    }, [i18nK, hostsRange]);

    return (
        <React.Fragment>
            {hostType === HostTypeFiled.SEGMENT ? null : (
                <CalculatorFormLayout.Row
                    title={i18nK('field_mdb-platform')}
                    note={i18nK('context_greenplum-platform')}
                >
                    <Field name={`${name}.${hostType}.platform`}>
                        {(fieldProps) => (
                            <FormFieldSelect<'single'>
                                {...fieldProps}
                                size="n"
                                items={toSelectItems(platforms)}
                                onUpdate={handlePlatformChange}
                            />
                        )}
                    </Field>
                </CalculatorFormLayout.Row>
            )}
            <CalculatorFormLayout.Row
                title={i18nK(`field_greenplum_${hostType}-type`)}
                note={i18nK(`context_greenplum_${hostType}-type`)}
            >
                <Field name={`${name}.${hostType}.type`}>
                    {(fieldProps) => (
                        <FormFieldRadioButton
                            {...fieldProps}
                            size="l"
                            options={hostTypes}
                            onUpdate={handleTypeChange}
                            width="max"
                        />
                    )}
                </Field>
            </CalculatorFormLayout.Row>
            <CalculatorFormLayout.Row
                title={i18nK(`field_greenplum_${hostType}-preset`)}
                note={i18nK(`context_greenplum_${hostType}-preset`)}
            >
                <Field name={`${name}.${hostType}.presetId`}>
                    {(fieldProps) => (
                        <FormFieldSelect<'single'>
                            {...fieldProps}
                            size="n"
                            items={toSelectItems(presets)}
                            onUpdate={handlePresetChange}
                        />
                    )}
                </Field>
            </CalculatorFormLayout.Row>
            <CalculatorFormLayout.Row
                title={i18nK(`field_greenplum_${hostType}-disk`)}
                note={i18nK(`context_greenplum_${hostType}-disk`)}
            >
                <Field name={`${name}.${hostType}.disk`}>
                    {(fieldProps) => (
                        <FormFieldSelect<'single'>
                            {...fieldProps}
                            size="n"
                            items={diskTypes}
                            onUpdate={handleDiskChange}
                        />
                    )}
                </Field>
            </CalculatorFormLayout.Row>
            <CalculatorFormLayout.Row
                title={i18nK(`field_greenplum_${hostType}-size`)}
                note={i18nK(`context_greenplum_${hostType}-size`)}
            >
                {diskSizes ? (
                    <SizeRangeStepInput
                        steps={diskSizes.sizes}
                        name={`${name}.${hostType}.size`}
                        infoPointsCount={2}
                        size="l"
                    />
                ) : (
                    <SizeRangeInput
                        min={Number(diskSizeRange?.min)}
                        max={Number(diskSizeRange?.max)}
                        name={`${name}.${hostType}.size`}
                        size="l"
                    />
                )}
            </CalculatorFormLayout.Row>
            {hostType === HostTypeFiled.MASTER ? null : (
                <HostsInput
                    name={name}
                    nameKey={`${hostType}.hosts`}
                    title={i18nK(`field_greenplum_${hostType}-hosts`)}
                    note={i18nK(`context_greenplum_${hostType}-hosts`)}
                    hostsRange={hostsRange}
                    handleChange={noop}
                    labels={hostsLabels}
                />
            )}
        </React.Fragment>
    );
};

function noop() {}

function getAvailablePlatforms(
    i18n: I18nFunction,
    availableGenerations: Set<string>,
    generationNameMap: Map<string, string>,
) {
    return GENERATION_ORDER.filter((gen) => availableGenerations.has(gen)).map((generation) => {
        let content;
        if (generation in GENERATION_ID_MAP) {
            const genId = GENERATION_ID_MAP[generation as keyof typeof GENERATION_ID_MAP];
            content = i18n('calculator', `value_mdb-${genId}`);
        } else {
            content = generationNameMap.get(generation) || '';
        }
        return {
            content,
            value: generation,
        };
    });
}

function getValidPreset(presets: Preset[], currentPresetId: string): Preset {
    return presets.find(({presetId}) => presetId === currentPresetId) || presets[0];
}

function getValidDiskType(diskTypes: DiskTypeProps[], currentDiskType: string) {
    const diskType = diskTypes.find(({value}) => value === currentDiskType) || diskTypes[0];

    return diskType.value;
}

function getDiskTypes(preset: Preset) {
    const availTypes = preset.diskTypes.map((diskType) => diskType.diskTypeId);

    return DISK_TYPE_ID_ORDER.filter((type) => {
        return availTypes.includes(type);
    }).map((type) => {
        return {
            title: type,
            value: type,
        };
    });
}

function getPresetName(i18n: I18nFunction, i18nK: I18nKFunction, preset: NormResourcePreset) {
    const {presetId, cpuLimit, memoryLimit, cpuFraction, type} = preset;
    const memoryFromBytes = getMemSizeLabel(memoryLimit, i18n);
    const memLabel = i18nK('field_memory_limit_value', {memory: memoryFromBytes});
    const cpuLabel = i18nK('field_cpu_limit_value', {count: cpuLimit});
    const units = i18nK('field_cpu_units', {count: cpuLimit});
    const fractionLabel = type === 'burstable' ? `, ${cpuFraction}%` : '';

    return `${presetId} (${cpuLabel} ${units} ${fractionLabel} / ${memLabel})`;
}

export default compose<GreenplumHostFormProps, GreenplumHostFormOwnProps>(
    withFinalForm<GreenplumFormValues>(),
    withTranslation('calculator'),
)(GreenplumFormItemHost);
